/**
 * api.ts — cửa máy chủ của tiểu đội Phòng KD (QĐ 0485): bảng tiểu đội / miền, hồ sơ tiểu đội
 * (tên hiệu, logo, khung, sổ đổi), nhãn tiểu đội của đơn ở màn làm việc. Máy chủ quyết ai thấy
 * gì, ai đổi được gì — màn chỉ vẽ.
 */
import { useContext, useMemo } from "react";
import {
  QueryClient,
  QueryClientContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api, apiBaseUrl } from "@/lib/api/client";
import { postFormAuthed } from "@/lib/api/authed-fetch";
import { serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";
import { rememberFreshUpload } from "@/lib/fresh-upload";
import { createBadgeBatcher, type OrderBadge } from "./badgeBatcher";

export type SquadBadge = components["schemas"]["SalesSquadBadge"];
export type SquadFrameRef = components["schemas"]["SalesSquadFrameRef"];
export type SquadBoard = components["schemas"]["SalesSquadBoard"];
export type SquadBoardRow = components["schemas"]["SalesSquadBoardRow"];
export type SquadRegionRow = components["schemas"]["SalesSquadRegionRow"];
export type SquadDetail = components["schemas"]["SalesSquadDetail"];
export type SquadFrameItem = components["schemas"]["SalesSquadFrameItem"];
export type SquadPresetTile = components["schemas"]["SalesSquadPresetTile"];
export type SquadEvent = components["schemas"]["SalesSquadEvent"];
export type SquadLogoUpload = components["schemas"]["SalesSquadLogoUpload"];
export type SquadTakedownWhat = components["schemas"]["SalesSquadTakedownInput"]["what"];
export type SquadPresetCode = components["schemas"]["SalesSquadPresetInput"]["preset"];
export type UnitFace = components["schemas"]["SalesUnitFace"];
export type UnitLogoUpload = components["schemas"]["SalesUnitLogoUpload"];

export const squadKeys = {
  all: ["sales-squad"] as const,
  board: (month: string) => ["sales-squad", "board", month] as const,
  detail: (id: string) => ["sales-squad", "detail", id] as const,
  badges: ["sales-squad", "order-badge"] as const,
  badge: (orderId: string) => ["sales-squad", "order-badge", orderId] as const,
};

/** Bảng tiểu đội + đường đua miền. month rỗng = tháng mới nhất xem được (máy chủ chọn). */
async function fetchBoard(month: string) {
  const { data, error } = await api.GET("/sales-squads/board", {
    params: { query: month ? { month } : {} },
  });
  if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được bảng tiểu đội"));
  return data;
}

export function useSquadBoard(month: string, enabled = true) {
  return useQuery({
    queryKey: squadKeys.board(month),
    enabled,
    queryFn: () => fetchBoard(month),
    staleTime: 60_000,
    // Bấm ‹ › giữ bảng tháng đang xem cho tới khi tháng mới về — thanh chọn tháng không nháy mất.
    placeholderData: (prev) => prev,
  });
}

export function useSquadDetail(id: string | null) {
  return useQuery({
    queryKey: squadKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await api.GET("/sales-squads/{id}", {
        params: { path: { id: id! } },
      });
      if (error || !data)
        throw new Error(serverErrorMessage(error, "Không tải được hồ sơ tiểu đội"));
      return data;
    },
  });
}

// Đổi bộ mặt một tiểu đội ⇒ hồ sơ lấy ngay bản máy chủ trả, bảng + nhãn đơn + chip bảng Sale
// (khoá "rankings") đọc lại cho khỏi hiện tên / logo cũ.
function afterChange(qc: QueryClient, detail: SquadDetail) {
  qc.setQueryData(squadKeys.detail(detail.squad.squadId), detail);
  void qc.invalidateQueries({ queryKey: [...squadKeys.all, "board"] });
  void qc.invalidateQueries({ queryKey: squadKeys.badges });
  void qc.invalidateQueries({ queryKey: ["rankings"] });
}

export function useSetNickname(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (nickname: string) => {
      const { data, error } = await api.PUT("/sales-squads/{id}/nickname", {
        params: { path: { id } },
        body: { nickname },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được tên hiệu"));
      return data;
    },
    onSuccess: (d) => afterChange(qc, d),
  });
}

export function useClearNickname(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.DELETE("/sales-squads/{id}/nickname", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không bỏ được tên hiệu"));
      return data;
    },
    onSuccess: (d) => afterChange(qc, d),
  });
}

export function useSetLogoPreset(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (preset: SquadPresetCode) => {
      const { data, error } = await api.PUT("/sales-squads/{id}/logo-preset", {
        params: { path: { id } },
        body: { preset },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không chọn được logo"));
      return data;
    },
    onSuccess: (d) => afterChange(qc, d),
  });
}

export function useUploadLogo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await postFormAuthed(
        `${apiBaseUrl}/sales-squads/${encodeURIComponent(id)}/logo`,
        form,
      );
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverErrorMessage(body, "Chưa tải được logo"));
      const out = body as SquadLogoUpload;
      // Biến thể ảnh còn đang dựng vài giây: vẽ bằng ảnh gốc cho khỏi xin trúng 404 bị ghim.
      rememberFreshUpload(out.basePath, out.origUrl);
      return out;
    },
    onSuccess: (d) => afterChange(qc, d),
  });
}

export function useClearLogo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.DELETE("/sales-squads/{id}/logo", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không bỏ được logo"));
      return data;
    },
    onSuccess: (d) => afterChange(qc, d),
  });
}

export function useSetFrame(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (choice: string) => {
      const { data, error } = await api.PUT("/sales-squads/{id}/frame", {
        params: { path: { id } },
        body: { choice },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không đổi được khung"));
      return data;
    },
    onSuccess: (d) => afterChange(qc, d),
  });
}

export function useTakedown(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { what: SquadTakedownWhat; reason: string }) => {
      const { data, error } = await api.POST("/sales-squads/{id}/takedown", {
        params: { path: { id } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gỡ được"));
      return data;
    },
    onSuccess: (d) => afterChange(qc, d),
  });
}

// Logo đội / miền (CEO 1b · 2a · 6a): chỉ ảnh tải riêng. Đổi xong ⇒ bảng đọc lại (khối logo miền
// & đội nằm trong cùng lần gọi bảng).
function afterUnitChange(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: [...squadKeys.all, "board"] });
}

export function useUploadUnitLogo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await postFormAuthed(
        `${apiBaseUrl}/sales-units/${encodeURIComponent(id)}/logo`,
        form,
      );
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverErrorMessage(body, "Chưa tải được logo"));
      const out = body as UnitLogoUpload;
      rememberFreshUpload(out.basePath, out.origUrl);
      return out;
    },
    onSuccess: () => afterUnitChange(qc),
  });
}

export function useClearUnitLogo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.DELETE("/sales-units/{id}/logo", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không bỏ được logo"));
      return data;
    },
    onSuccess: () => afterUnitChange(qc),
  });
}

export function useTakedownUnitLogo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string) => {
      const { data, error } = await api.POST("/sales-units/{id}/takedown", {
        params: { path: { id } },
        body: { reason },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không gỡ được"));
      return data;
    },
    onSuccess: () => afterUnitChange(qc),
  });
}

async function fetchOrderBadges(orderIds: string[]) {
  const { data, error } = await api.POST("/sales-squads/order-badges", { body: { orderIds } });
  if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được nhãn tiểu đội"));
  return data;
}

const batcher = createBadgeBatcher(fetchOrderBadges);

/**
 * Nhãn tiểu đội của MỘT đơn ở màn làm việc. Mọi ô cùng nhịp vẽ gom thành một lần gọi (lô 200);
 * nhớ 5 phút theo mã đơn. Tắt (`enabled` false) với người trong — họ thấy tên bạn sale.
 */
export function useOrderSquadBadge(orderId: string | null | undefined, enabled: boolean) {
  // Nhãn là thành phần lá ở ~10 màn; cây nào không có QueryClientProvider (thành phần dựng riêng
  // lẻ) thì không gọi mạng và màn giữ tên bạn sale — thay vì ném lỗi làm sập cả màn (cùng cách
  // ảnh đại diện, lib/staff-avatar.ts).
  const provided = useContext(QueryClientContext);
  return useQuery<OrderBadge>(
    {
      queryKey: squadKeys.badge(orderId ?? ""),
      enabled: enabled && !!orderId && !!provided,
      queryFn: () => batcher.load(orderId!),
      staleTime: 5 * 60_000,
      retry: 1,
    },
    provided ?? idleClient,
  );
}

const idleClient = new QueryClient();

/**
 * Logo tiểu đội / đội / miền theo mã (bảng tháng mới nhất, chung bộ nhớ với thẻ «06 Tiểu đội») cho thành phần
 * lá ở màn khác — màn Phòng Kinh doanh (CEO 30/10/2026). Cây không có QueryClientProvider hay
 * người xem không được xem bảng ⇒ rỗng, nơi gọi lùi về chữ cái đầu, không ném lỗi.
 */
export function useSquadBadgeMap(): Map<string, Pick<SquadBadge, "name"> & Partial<SquadBadge>> {
  const provided = useContext(QueryClientContext);
  const board = useQuery(
    {
      queryKey: squadKeys.board(""),
      enabled: !!provided,
      queryFn: () => fetchBoard(""),
      staleTime: 60_000,
      retry: 1,
    },
    provided ?? idleClient,
  );
  // Cùng bảng trả logo đội / miền (QĐ 0492, `units`) — tra theo mã đơn vị như tiểu đội. `units` có cả
  // tiểu đội (QĐ 0499): dòng của `rows` đầy đủ hơn (khung), đừng đè; chỉ thêm tiểu đội chưa có dòng.
  return useMemo(() => {
    const out = new Map<string, Pick<SquadBadge, "name"> & Partial<SquadBadge>>();
    for (const r of board.data?.rows ?? []) out.set(r.squad.squadId, r.squad);
    for (const u of board.data?.units ?? []) {
      if (out.has(u.unitId)) continue;
      out.set(u.unitId, {
        name: u.name,
        ...(u.logo ? { logo: u.logo } : {}),
        ...(u.preset ? { preset: u.preset } : {}),
      });
    }
    return out;
  }, [board.data]);
}
